// YouTube-first lesson player.
//
// Renders the official YouTube IFrame embed (www.youtube.com, NOT nocookie —
// the privacy domain suppresses ad personalization and with it most embed
// revenue; monetization is the point of YouTube-first). If the IFrame API
// fails to arrive (network block, ad-blocker, region), or the player errors
// (video private/removed/embeds disabled), we call onFail and the page swaps
// in the self-hosted player — same lesson, zero dead screens.
//
// Progress still lands in the same localStorage store: we poll
// getCurrentTime() every 5s while playing.
import { disableCaptions } from "@/lib/youtubePlayer";
import { useEffect, useRef } from "react";

declare global {
  interface Window {
    YT?: any;
    onYouTubeIframeAPIReady?: () => void;
  }
}

interface Props {
  youtubeId: string;
  startSeconds?: number;
  onReady?: (player: any) => void;
  onProgress: (pos: number, dur: number) => void;
  onEnded: () => void;
  onPlay?: () => void;
  onFail: () => void;
  /** Theater mode: fill the parent's height instead of forcing 16:9. */
  fill?: boolean;
}

let apiPromise: Promise<any> | null = null;

function loadIframeApi(timeoutMs = 4500): Promise<any> {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (!apiPromise) {
    apiPromise = new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        apiPromise = null; // allow a retry on next mount
        reject(new Error("YT IFrame API timeout"));
      }, timeoutMs);
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        clearTimeout(timer);
        prev?.();
        resolve(window.YT);
      };
      const s = document.createElement("script");
      s.src = "https://www.youtube.com/iframe_api";
      s.async = true;
      s.onerror = () => {
        clearTimeout(timer);
        apiPromise = null;
        reject(new Error("YT IFrame API script error"));
      };
      document.head.appendChild(s);
    });
  }
  return apiPromise;
}

export default function YouTubeLessonPlayer({
  youtubeId, startSeconds, onReady, onProgress, onEnded, onPlay, onFail, fill = false,
}: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const pollRef = useRef<number | null>(null);

  useEffect(() => {
    let dead = false;

    const stopPoll = () => {
      if (pollRef.current !== null) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };

    loadIframeApi()
      .then((YT) => {
        if (dead || !hostRef.current) return;
        playerRef.current = new YT.Player(hostRef.current, {
          videoId: youtubeId,
          playerVars: {
            rel: 0,
            playsinline: 1,
            // Our videos carry burned-in captions; keep YouTube's layer off (see lib/youtubePlayer).
            cc_load_policy: 3,
            start: startSeconds ? Math.floor(startSeconds) : undefined,
            origin: window.location.origin,
            // legacy quality hint — officially ignored, occasionally honored
            vq: "hd1080",
          },
          events: {
            onReady: (e: any) => {
              // YouTube auto-picks quality from rendered player size; the API
              // hint is best-effort but costs nothing, so ask anyway.
              try { e.target.setPlaybackQuality?.("hd1080"); } catch { /* removed API */ }
              disableCaptions(e.target);
              onReady?.(e.target);
            },
            onError: () => {
              // 2/5 bad id, 100 removed, 101/150 embeds disabled — all mean
              // "our copy should play instead."
              stopPoll();
              onFail();
            },
            onStateChange: (e: any) => {
              const YTS = window.YT?.PlayerState;
              if (!YTS) return;
              if (e.data === YTS.PLAYING) {
                // re-assert on first play: quality decisions happen at stream
                // start, and the size-based pick sometimes lands low on a
                // fast first paint before layout settles
                try { e.target.setPlaybackQuality?.("hd1080"); } catch { /* removed API */ }
                onPlay?.();
                stopPoll();
                pollRef.current = window.setInterval(() => {
                  const p = playerRef.current;
                  if (!p?.getCurrentTime) return;
                  onProgress(p.getCurrentTime(), p.getDuration() || 0);
                }, 5000);
              } else if (e.data === YTS.ENDED) {
                stopPoll();
                onEnded();
              } else {
                stopPoll();
              }
            },
          },
        });
      })
      .catch(() => {
        if (!dead) onFail();
      });

    return () => {
      dead = true;
      stopPoll();
      try { playerRef.current?.destroy?.(); } catch { /* iframe already gone */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [youtubeId]);

  return (
    <div style={{ width: "100%", background: "#000", ...(fill ? { height: "100%" } : { aspectRatio: "16/9" }) }}>
      <div ref={hostRef} style={{ width: "100%", height: "100%" }} />
    </div>
  );
}

/** youtu.be/xyz, youtube.com/watch?v=xyz, /embed/xyz, /shorts/xyz → "xyz". */
export function extractYouTubeId(url: string | null): string | null {
  if (!url) return null;
  const m = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/))([A-Za-z0-9_-]{6,})/,
  );
  return m ? m[1] : null;
}
